/**
 * Unified auth context.
 * strategy = 'firebase' | 'jwt'   (default: 'firebase' when env unset)
 * Architecture: Firebase Auth on frontend + backend token validation + MongoDB user/role mapping.
 */
import PropTypes from 'prop-types';
import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';

import { API_BASE_URL } from '../config/api.config';
import logger from '../utils/logger';
import jwtAdapter from '../auth/adapters/jwtAdapter.jsx';
import firebaseAdapter from '../auth/adapters/firebaseAdapter.jsx';
import { registerTokenRefresh } from '../utils/authToken';

const AuthContext = createContext(null);

const getAdapterName = (adapter) => {
  if (adapter === jwtAdapter) return 'jwtAdapter';
  if (adapter === firebaseAdapter) return 'firebaseAdapter';
  return 'unknown';
};

const AuthProviderUnified = ({ strategy, children }) => {
  const envStrategy = import.meta.env.VITE_AUTH_STRATEGY;
  const defaultStrategy = strategy || envStrategy || 'firebase';
  const adapter = useMemo(() => {
    switch (defaultStrategy) {
      case 'jwt':
        return jwtAdapter;
      case 'firebase':
        return firebaseAdapter;
      default:
        throw new Error(`Unknown auth strategy: ${defaultStrategy}`);
    }
  }, [defaultStrategy]);

  // Validate adapter has required methods
  if (
    !adapter ||
    typeof adapter.subscribe !== 'function' ||
    typeof adapter.signIn !== 'function' ||
    typeof adapter.signOut !== 'function'
  ) {
    throw new Error(`Invalid auth adapter for strategy: ${defaultStrategy}`);
  }

  const [user, setUser] = useState(null);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Startup log: strategy + API base URL + adapter name
  useEffect(() => {
    logger.info('[Auth] strategy=%s adapter=%s apiBaseUrl=%s', {
      strategy: defaultStrategy,
      adapter: getAdapterName(adapter),
      apiBaseUrl: API_BASE_URL || '(unset)',
    });
  }, [defaultStrategy, adapter]);

  useEffect(() => {
    setLoading(true);
    const unsubscribe = adapter.subscribe((user) => {
      setUser(user);
      setLoading(false);
    }, setRoles);
    return unsubscribe;
  }, [adapter]);

  useEffect(() => {
    if (typeof adapter.refreshAuthToken === 'function') {
      registerTokenRefresh(() => adapter.refreshAuthToken());
    } else {
      registerTokenRefresh(null);
    }
    return () => registerTokenRefresh(null);
  }, [adapter]);

  const login = async (email, password) => {
    try {
      setError(null);
      const user = await adapter.signIn(email, password);
      setUser(user);
      return user;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const logout = async () => {
    try {
      await adapter.signOut();
      setUser(null);
      setRoles([]);
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const register = async (email, password, userData = {}) => {
    try {
      setError(null);
      // Check if adapter supports registration
      if (adapter.register && typeof adapter.register === 'function') {
        const user = await adapter.register(email, password, userData);
        setUser(user);
        return user;
      } else {
        throw new Error('Registration not supported by current auth adapter');
      }
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const value = {
    user,
    roles,
    loading,
    error,
    login,
    logout,
    register,
    signIn: adapter.signIn,
    signOut: adapter.signOut,
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);

AuthProviderUnified.propTypes = {
  strategy: PropTypes.oneOf(['firebase', 'jwt']),
  children: PropTypes.node.isRequired,
};

export { AuthProviderUnified };
